import { useEffect, useRef } from 'react';
import { isHlsSource } from '@/core/utils/resolvePostMediaUrl';

const FETCH_OPTIONS: RequestInit = { referrerPolicy: 'no-referrer', priority: 'low' };
const INIT_SEGMENT_URI_REGEX = /#EXT-X-MAP:.*URI="([^"]+)"/;

async function fetchPlaylistText(url: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(url, { ...FETCH_OPTIONS, signal });
  if (!response.ok) {
    throw new Error(`Unable to load HLS playlist: ${response.status}`);
  }
  return response.text();
}

function getPlaylistMediaUris(playlist: string): string[] {
  return playlist
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));
}

async function fetchSegmentBuffers(segmentUrls: string[], signal?: AbortSignal): Promise<ArrayBuffer[]> {
  const responses = await Promise.all(segmentUrls.map((url) => fetch(url, { ...FETCH_OPTIONS, signal })));
  const failedResponse = responses.find((response) => !response.ok);
  if (failedResponse) {
    throw new Error(`Unable to load HLS media segment: ${failedResponse.status}`);
  }
  return Promise.all(responses.map((response) => response.arrayBuffer()));
}

async function fetchMediaPlaylist(url: string, signal?: AbortSignal): Promise<{ url: string; text: string }> {
  const playlist = await fetchPlaylistText(url, signal);
  if (/^#EXTINF:/m.test(playlist)) {
    return { url, text: playlist };
  }

  const [mediaUri] = getPlaylistMediaUris(playlist);
  if (!mediaUri) {
    throw new Error('Invalid HLS master playlist');
  }

  const mediaUrl = new URL(mediaUri, url).href;
  return { url: mediaUrl, text: await fetchPlaylistText(mediaUrl, signal) };
}

export async function buildHlsPlaybackUrl(masterUrl: string, signal?: AbortSignal): Promise<string> {
  const playlist = await fetchMediaPlaylist(masterUrl, signal);
  const initMatch = INIT_SEGMENT_URI_REGEX.exec(playlist.text);
  const segmentUris = getPlaylistMediaUris(playlist.text);
  if (!initMatch || !segmentUris.length) {
    throw new Error('Invalid HLS media playlist');
  }

  const segmentUrls = [initMatch[1], ...segmentUris].map((uri) => new URL(uri, playlist.url).href);
  const buffers = await fetchSegmentBuffers(segmentUrls, signal);

  return URL.createObjectURL(new Blob(buffers, { type: 'video/mp4' }));
}

export function useVideoSource(src: string, mimeType: string | undefined, onError: () => void) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    if (!isHlsSource(src, mimeType)) {
      video.src = src;
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | undefined;
    const controller = new AbortController();

    buildHlsPlaybackUrl(src, controller.signal)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        video.src = url;
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('error rebuilding HLS video source', error);
        onErrorRef.current();
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, mimeType]);

  return videoRef;
}
